import { thousandthFormat } from '@/pages/home/utils/common';
import { buildDownloadDataNew } from '@intelligence/utils/commonUtils';
import { formatRobloxMetricRatioText } from '@intelligence/v3/utils/robloxGrowthFormat';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import { GLOBAL_REGION } from '@intelligence/v3/constants/region';
import { DataCalculation } from '@intelligence/v3/enums/download';
import { getCountryByCode } from '@intelligence/v3/utils/region';
import { formatRobloxSupportFeatureEnText } from '@intelligence/v3/pages/RobloxTopCharts/utils/formatRobloxSupportFeature';
import { sortDownloadDataByConfig } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import {
  ROBLOX_TOP_CHARTS_DOWNLOAD_COLUMN_META,
} from './robloxTopChartsDownloadMeta';

const GLOBAL_COUNTRY_CH = GLOBAL_REGION.country_set?.[0]?.country_name_ch || '全球';
const GLOBAL_COUNTRY_EN = GLOBAL_REGION.country_set?.[0]?.country_name_en || 'Global';

/** 指标值：千分位字符串，避免 Excel 将大数显示为 E+12 */
const formatMetricCellValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numericValue =
    typeof value === 'number' ? value : Number(String(value).trim());

  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return String(thousandthFormat(numericValue, 0));
};

/** 变化率：与表格一致，使用 growthFormat 格式化后导出文本 */
const formatRatioCellValue = (value: unknown): string => formatRobloxMetricRatioText(
  value === null || value === undefined || value === '' ? null : Number(value),
);

const getSubGenre = (row: IRecord, entityInfoMap: IRecord): string => {
  const gameId = row?.game_id || '';
  const entityInfo = entityInfoMap?.[gameId] || {};

  return row?.roblox_level_3_genre || entityInfo?.roblox_level_3_genre || '';
};

const mergeRobloxTopChartsDownloadRow = (
  item: IRecord,
  entityInfoMap: IRecord,
): IRecord => {
  const gameId = item?.game_id || '';
  const entityInfo = gameId ? entityInfoMap?.[gameId] : undefined;

  return {
    ...item,
    ...(entityInfo || {}),
    entity_info: entityInfo,
  };
};

const getGameName = (item: IRecord, entityInfoMap: IRecord): string => {
  const gameId = item?.game_id || '';

  return (
    entityInfoMap?.[gameId]?.entity_name ||
    item?.entity_name ||
    gameId ||
    ''
  );
};

const getMainGenre = (item: IRecord, entityInfoMap: IRecord): string => {
  const gameId = item?.game_id || '';

  return item?.roblox_sub_genre || entityInfoMap?.[gameId]?.roblox_sub_genre || '';
};

const getRobloxGenreDisplay = (item: IRecord, entityInfoMap: IRecord): string => {
  const gameId = item?.game_id || '';
  const entityInfo = entityInfoMap?.[gameId] || {};
  const mainGenre = item?.roblox_sub_genre || entityInfo?.roblox_sub_genre || '';
  const subGenre = item?.roblox_level_3_genre || entityInfo?.roblox_level_3_genre || '';

  if (mainGenre && subGenre) {
    return `${mainGenre} / ${subGenre}`;
  }

  return mainGenre || subGenre || '';
};

const getReleaseDate = (
  item: IRecord,
  entityInfoMap: IRecord,
  isGroupByDate: boolean,
): string => {
  const gameId = item?.game_id || '';
  const releaseTime = item?.release_time || entityInfoMap?.[gameId]?.release_time;

  if (releaseTime) {
    return releaseTime;
  }

  return isGroupByDate ? (item?.date || '') : '';
};

const getUpdatedDate = (
  item: IRecord,
  entityInfoMap: IRecord,
  isGroupByDate: boolean,
): string => {
  const gameId = item?.game_id || '';
  const updateTime = item?.update_time || entityInfoMap?.[gameId]?.update_time;

  if (isGroupByDate) {
    return item?.date || updateTime || '';
  }

  return updateTime || '';
};

const getCountryChinese = (
  item: IRecord,
  options: {
    isGroupByRegion: boolean;
    isGroupByMarket: boolean;
    regionNameCNMap: IRecord;
    marketNameCNMap: IRecord;
    regionCodeMap: IRecord;
    regionOptions: IRecord[];
  },
): string => {
  const {
    isGroupByRegion,
    isGroupByMarket,
    regionNameCNMap,
    marketNameCNMap,
    regionCodeMap,
    regionOptions,
  } = options;

  if (!isGroupByRegion && !isGroupByMarket) {
    return GLOBAL_COUNTRY_CH;
  }

  if (isGroupByRegion) {
    const itemRegionCode = (item?.region || '').toLowerCase();
    return regionNameCNMap?.[itemRegionCode] || itemRegionCode || '';
  }

  const itemMarketCode = item?.market || '';
  if (!itemMarketCode || itemMarketCode === 'global') {
    return GLOBAL_COUNTRY_CH;
  }

  return (
    marketNameCNMap?.[itemMarketCode.toLowerCase()] ||
    getCountryByCode(itemMarketCode, regionOptions as IRegion[])?.country_name_ch ||
    itemMarketCode ||
    ''
  );
};

const getCountryEnglish = (
  item: IRecord,
  options: {
    isGroupByRegion: boolean;
    isGroupByMarket: boolean;
    regionNameENMap: IRecord;
    marketNameENMap: IRecord;
    regionCodeMap: IRecord;
    regionOptions: IRecord[];
  },
): string => {
  const {
    isGroupByRegion,
    isGroupByMarket,
    regionNameENMap,
    marketNameENMap,
    regionOptions,
  } = options;

  if (!isGroupByRegion && !isGroupByMarket) {
    return GLOBAL_COUNTRY_EN;
  }

  if (isGroupByRegion) {
    const itemRegionCode = (item?.region || '').toLowerCase();
    return regionNameENMap?.[itemRegionCode] || itemRegionCode || '';
  }

  const itemMarketCode = item?.market || '';
  if (!itemMarketCode || itemMarketCode === 'global') {
    return GLOBAL_COUNTRY_EN;
  }

  return (
    marketNameENMap?.[itemMarketCode.toLowerCase()] ||
    getCountryByCode(itemMarketCode, regionOptions as IRegion[])?.country_name_en ||
    itemMarketCode ||
    ''
  );
};

const buildOptionalMetricColumns = (
  allParams: IRecord,
  entityInfoMap: IRecord,
  isRatioShow: boolean,
) => {
  const columns: IRecord[] = [];

  ROBLOX_TOP_CHARTS_DOWNLOAD_COLUMN_META.forEach((option) => {
    const isShow = allParams?.[option.showKey] === 1;

    if (option.metric) {
      columns.push({
        title: option.exportTitle,
        key: (item: IRecord) => formatMetricCellValue(item?.[option.metric as string]),
        when: () => isShow,
      });

      if (option.hasRatio && option.ratioMetric) {
        columns.push({
          title: '%',
          key: (item: IRecord) =>
            formatRatioCellValue(item?.[option.ratioMetric as string]),
          when: () => isShow && isRatioShow,
        });
      }

      return;
    }

    if (option.customField) {
      columns.push({
        title: option.exportTitle,
        key: (item: IRecord) => {
          if (option.customField === 'support_feature') {
            return formatRobloxSupportFeatureEnText(item?.support_feature);
          }

          return item?.[option.customField as string] || '';
        },
        when: () => isShow,
      });
      return;
    }

    if (option.showKey === 'roblox_genre_show') {
      columns.push({
        title: option.exportTitle,
        key: (item: IRecord) => getRobloxGenreDisplay(item, entityInfoMap),
        when: () => isShow,
      });
    }
  });

  return columns;
};

export const getRobloxTopChartsConfig = (data: any, params: any, allParams: any) => {
  const entityInfoMap =
    data?.$ext?.entity_info ||
    data?.ext?.entity_info ||
    data?.data?.ext?.entity_info ||
    {};
  const marketInfo =
    data?.$ext?.market_info ||
    data?.ext?.market_info ||
    data?.data?.ext?.market_info ||
    {};
  const rawList = Array.isArray(data?.list)
    ? data.list
    : Array.isArray(data?.data?.list)
      ? data.data.list
      : [];

  const regionOptions = allParams?.regionOptions || [];
  const isGroupByDate = allParams?.[DOWNLOAD_CFG_KEY.DATE_METHOD] !== DataCalculation.Aggregated;
  const isGroupByRegion =
    allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByRegion;
  const isGroupByMarket =
    allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByCountry;
  const isRatioShow = allParams?.ratio_show === 1;

  const regionNameENMap = marketInfo?.['intel_region -> en'] || {};
  const regionNameCNMap = marketInfo?.['intel_region -> cn'] || {};
  const marketNameENMap = marketInfo?.['market -> en'] || {};
  const marketNameCNMap = marketInfo?.['market -> cn'] || {};
  const regionCodeMap = marketInfo?.['market -> region'] || {};

  const countryOptions = {
    isGroupByRegion,
    isGroupByMarket,
    regionNameENMap,
    regionNameCNMap,
    marketNameENMap,
    marketNameCNMap,
    regionCodeMap,
    regionOptions,
  };

  const defaultSheetConfig = [
    {
      title: 'Game Name',
      key: (item: IRecord) => getGameName(item, entityInfoMap),
    },
    {
      title: 'Main Genre',
      key: (item: IRecord) => getMainGenre(item, entityInfoMap),
    },
    {
      title: 'Sub Genre',
      key: (item: IRecord) => getSubGenre(item, entityInfoMap),
    },
    {
      title: 'Country_Chinese',
      key: (item: IRecord) => getCountryChinese(item, countryOptions),
    },
    {
      title: 'Country_English',
      key: (item: IRecord) => getCountryEnglish(item, countryOptions),
    },
    {
      title: 'Release Date',
      key: (item: IRecord) => getReleaseDate(item, entityInfoMap, isGroupByDate),
    },
    {
      title: 'Updated Date',
      key: (item: IRecord) => getUpdatedDate(item, entityInfoMap, isGroupByDate),
    },
  ];

  const optionalSheetConfig = buildOptionalMetricColumns(
    allParams,
    entityInfoMap,
    isRatioShow,
  );

  const commonSheetConfig = [...defaultSheetConfig, ...optionalSheetConfig];

  const listForSort = rawList
    .map((item: IRecord) => mergeRobloxTopChartsDownloadRow(item, entityInfoMap))
    .map((item: IRecord) => ({
      ...item,
      _region: item?.region || regionCodeMap?.[item?.market]?.toLowerCase() || '',
    }));

  const sortedList = sortDownloadDataByConfig(listForSort, {
    entityKey: 'game_id',
    isGroupByDate,
    regionMethod: allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD],
    regionKey: '_region',
    marketKey: 'market',
  });

  return {
    stat: {
      sheetName: 'Roblox Top Charts',
      style: 'normal',
      data: buildDownloadDataNew(commonSheetConfig, sortedList),
    },
    summary: {
      sheetName: 'Summary',
      style: 'summary',
      data: [
        ['Regions/Countries', Array.isArray(params?.markets) ? params?.markets?.join(', ') : ''],
        ['Date Start', params?.start_date || ''],
        ['Date End', params?.end_date || ''],
      ],
    },
  };
};
